import React, { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CampaignTableFiltersProps, VoohCampaignsFiltersForm } from './types'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons'
import Card from 'components/Layout/Card'
import InputSearch from 'components/Form/InputSearch'
import DatePickerSingle from 'components/Form/DatePickerSingle'
import { DATE_PLACEHOLDER } from 'constant'
import FormRow from 'components/Form/FormRow'
import service from './service'
import SelectMultiCheckbox from 'components/Form/SelectMultiCheckbox'
import { SelectOption } from 'components/Form/Select'
import CampaignUtils from 'utils/campaign'
import { CampaignStatus } from 'types/campaign'
import FillingSpinner, { FillingSpinnerType } from 'components/FillingSpinner'
import TsxUtils from 'utils/tsx'
import EnumUtils from 'utils/enum'
import { useDispatch, useSelector } from 'react-redux'
import { setCollapsed, setVoohCampaignsFilters } from './slice'
import { AppDispatch, RootState } from 'store'
import DatePickerRangeSeparate from 'components/Form/DatePickerRangeSeparate'
import './VoohCampaignTableFilters.scss'
import i18n from '../../../i18n'

const VoohCampaignTableFilters: React.FC<CampaignTableFiltersProps> = ({ onSubmit }) => {
  const [startDateFrom, setStartDateFrom] = React.useState('')
  const [endDateFrom, setEndDateFrom] = React.useState('')
  const [startDateTo, setStartDateTo] = React.useState('')
  const [endDateTo, setEndDateTo] = React.useState('')
  const { t } = useTranslation()
  const { voohCampaignfilters, collapsed, loading } = useSelector(
    (rs: RootState) => rs.voohCampaignTableFilters
  )
  const dispatch: AppDispatch = useDispatch()

  useEffect(() => {
    if (!collapsed && service.areFiltersFetched(voohCampaignfilters)) {
      service.getFilters(dispatch)
    }
  }, [collapsed, voohCampaignfilters])

  const updateFilters = (filtersSubset: Partial<VoohCampaignsFiltersForm>): void => {
    void dispatch(setVoohCampaignsFilters({ ...voohCampaignfilters, ...filtersSubset }))
  }

  const SearchRow = (): ReactElement => (
    <div className='CampaignTableFilters__search-row'>
      <Button
        theme={ButtonTheme.NAVY_BLUE_OUTLINE}
        size={ButtonSize.SMALL}
        icon={collapsed ? faCaretDown : faCaretUp}
        onClick={() => void dispatch(setCollapsed(!collapsed))}
      >
        {t('common.filters')}
      </Button>

      <InputSearch
        id='text'
        value={voohCampaignfilters.text}
        onChange={text => void dispatch(setVoohCampaignsFilters({ ...voohCampaignfilters, text }))}
        onSubmit={text => {
          dispatch(setVoohCampaignsFilters({ ...voohCampaignfilters, text }))
          onSubmit()
        }}
      />
    </div>
  )

  const Container = (loading: boolean): ReactElement => (
    <Card
      className={
        'CampaignTableFilters__container' +
        TsxUtils.extraStyle(loading, 'CampaignTableFilters__container--loading')
      }
    >
      {loading ? <FillingSpinner type={FillingSpinnerType.STANDARD} /> : Filters()}
    </Card>
  )

  const isNotAllowedDate = (
    day: Date,
    dateTo: string,
    dateFrom: string,
    isDateFromDatePicker: boolean
  ): boolean => {
    if (isDateFromDatePicker) {
      return day > new Date(dateTo)
    }
    return day < new Date(dateFrom)
  }

  const resetDatesState = () => {
    setStartDateFrom('')
    setStartDateTo('')
    setEndDateFrom('')
    setEndDateTo('')
  }

  const Filters = (): ReactElement => (
    <FormRow>
      <div className='CampaignTableFilters__filter'>
        <SelectMultiCheckbox
          id='agencies'
          title={t('common.agency')}
          value={voohCampaignfilters.agencies}
          options={voohCampaignfilters.agenciesAll.map(
            (c: string): SelectOption => ({ value: c, label: c })
          )}
          onChange={(agencies): void => updateFilters({ agencies })}
          selectAllOption
        />
      </div>

      <div className='CampaignTableFilters__range-filter'>
        <DatePickerRangeSeparate title={t('filters.start')}>
          <DatePickerSingle
            id='startDateFrom'
            value={voohCampaignfilters.startDateFrom}
            placeholder={DATE_PLACEHOLDER}
            isOutsideRange={date => isNotAllowedDate(date, startDateTo, startDateFrom, true)}
            onChange={(startDateFrom): void => {
              updateFilters({ startDateFrom })
              setStartDateFrom(startDateFrom)
            }}
          />
          <DatePickerSingle
            id='startDateTo'
            value={voohCampaignfilters.startDateTo}
            placeholder={DATE_PLACEHOLDER}
            isOutsideRange={date => isNotAllowedDate(date, startDateTo, startDateFrom, false)}
            onChange={(startDateTo): void => {
              updateFilters({ startDateTo })
              setStartDateTo(startDateTo)
            }}
          />
        </DatePickerRangeSeparate>

        <DatePickerRangeSeparate title={t('filters.end')}>
          <DatePickerSingle
            id='endDateFrom'
            value={voohCampaignfilters.endDateFrom}
            placeholder={DATE_PLACEHOLDER}
            isOutsideRange={date => isNotAllowedDate(date, endDateTo, endDateFrom, true)}
            onChange={(endDateFrom): void => {
              updateFilters({ endDateFrom })
              setEndDateFrom(endDateFrom)
            }}
          />
          <DatePickerSingle
            id='endDateTo'
            value={voohCampaignfilters.endDateTo}
            placeholder={DATE_PLACEHOLDER}
            isOutsideRange={date => isNotAllowedDate(date, endDateTo, endDateFrom, false)}
            onChange={(endDateTo): void => {
              updateFilters({ endDateTo })
              setEndDateTo(endDateTo)
            }}
          />
        </DatePickerRangeSeparate>
      </div>

      <div className='CampaignTableFilters__filter'>
        <SelectMultiCheckbox
          id='supervisors'
          title={t('common.supervisors')}
          value={voohCampaignfilters.supervisors}
          options={voohCampaignfilters.supervisorsAll
            .map(
              (supervisor: string): SelectOption => ({
                value: supervisor,
                label: supervisor,
              })
            )
            .sort((a, b) => a.label.localeCompare(b.label))}
          onChange={(supervisors): void => updateFilters({ supervisors })}
          selectAllOption
        />
      </div>

      <div className='CampaignTableFilters__filter--states'>
        <SelectMultiCheckbox
          id='statuses'
          title={t('common.status')}
          value={voohCampaignfilters.statuses}
          options={[
            ...voohCampaignfilters.statusesAll.map(
              (selectOption: string): SelectOption => ({
                value: selectOption,
                label: CampaignUtils.parseStatus(
                  CampaignStatus[EnumUtils.getKeyByValueT(CampaignStatus, selectOption)!]
                ).text,
              })
            ),
            {
              value: 'pending_acceptation',
              label: i18n.t('campaignStatus.pendingAcceptation'),
            },
          ]}
          onChange={(statuses): void => {
            updateFilters({ statuses })
          }}
          selectAllOption
        />
        <SelectMultiCheckbox
          id='brandNetwork'
          title={t('common.brandNetwork')}
          value={voohCampaignfilters.brandOrNetworkSelected || []}
          options={[
            {
              value: 'yes',
              label: t('common.yes'),
            },
            {
              value: '',
              label: t('common.no'),
            },
          ]}
          onChange={(brandOrNetworkSelected): void => updateFilters({ brandOrNetworkSelected })}
          selectAllOption
        />
      </div>

      <Actions />
    </FormRow>
  )

  const Actions = (): ReactElement => (
    <div className='CampaignTableFilters__actions'>
      <Button
        size={ButtonSize.SMALL}
        theme={ButtonTheme.BLUE_OUTLINE}
        onClick={() => {
          resetDatesState()
          service.cleanFiltersForm(voohCampaignfilters, dispatch)
          onSubmit()
        }}
      >
        {t('filters.clearFilters')}
      </Button>

      <Button
        size={ButtonSize.SMALL}
        onClick={() => void onSubmit()}
      >
        {t('filters.filter')}
      </Button>
    </div>
  )

  return (
    <div className='CampaignTableFilters'>
      {SearchRow()}
      {!collapsed && Container(loading)}
    </div>
  )
}

export default VoohCampaignTableFilters
